'use client'

import { FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supportApi } from '../api/endpoints'
import { SupportGlyph } from '../components/lucky/ProfileGlyphs'
import { showToast } from '../components/ui/Toast'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'
import { formatDate } from '../utils/helpers'

export default function SupportPage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale, dir } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['support', 'chat'],
    queryFn: () => supportApi.getChat(),
    refetchInterval: 5000,
  })

  const ticket = data?.ticket
  const messages = ticket?.messages || []

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const sendMutation = useMutation({
    mutationFn: (text: string) => supportApi.sendMessage(text),
    onSuccess: (res) => {
      setDraft('')
      queryClient.setQueryData(['support', 'chat'], { ticket: res.ticket })
    },
    onError: (err: any) => showToast(err.response?.data?.message || t('support.sendErr'), 'error'),
  })

  const send = () => {
    const text = draft.trim()
    if (!text || sendMutation.isPending) return
    sendMutation.mutate(text)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    send()
  }

  return (
    <div className="lucky-support mini-app-container pb-20">
      <div className="lucky-support__inner">
        <header className="lucky-support__head">
          <button type="button" className="lucky-support__back" onClick={() => navigate('/')}>
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-support__icon" aria-hidden="true">
            <SupportGlyph size={36} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="lucky-support__title">{t('support.title')}</h1>
            <p className="lucky-support__meta">{t('support.meta')}</p>
          </div>
        </header>

        <div className="lucky-support__chat">
          <div className="lucky-support__messages">
            {isLoading && messages.length === 0 ? (
              <p className="lucky-support__empty">{t('common.loading')}</p>
            ) : messages.length === 0 ? (
              <p className="lucky-support__empty">{t('support.empty')}</p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`lucky-support__bubble lucky-support__bubble--${m.sender === 'user' ? 'me' : 'them'}`}
                >
                  <p>{m.text}</p>
                  <time>{formatDate(m.created_at, numberLocale)}</time>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          <form className="lucky-support__composer" onSubmit={submit}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder={t('support.placeholder')}
              rows={2}
              maxLength={2000}
              disabled={sendMutation.isPending}
            />
            <button type="submit" disabled={sendMutation.isPending || !draft.trim()}>
              {t('common.send')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
